import { type ExplorationSpec } from '@/designs-dev/explore/types';

import {
  // A1ShortlistInRail, A2ShortlistPage, B2RowDrawer — used by the hidden
  // sections at the foot.
  F0BuildShortlist,
  F1SendRequest,
  F1bRowDrawer,
  F2Email,
  F3Paywall,
  F4Answers,
  F6Unlocked,
  F7Push,
  F8MemberShortlist,
  F9CamShortlist,
  F11Timeout,
  F12PublishUnpaid,
  S2PaymentFailed,
} from '@/cam-admin/explore/paywall/Flow';

// One section per screen. The flow used to be a single sequence section, which
// put nine screens in one horizontal strip and buried the questions under it.
// Split out, each screen gets its own heading in the jump menu and its brief
// sits beside it — so the page reads in the order the flow happens, and the
// thing each screen is chasing is the first thing you see next to it. What was
// the `tap` between frames is now the opening line of the section's intent.
//
// Redrawn against the spec of 12 Aug. Two changes in it moved the flow rather
// than the copy: the round of availability calls is deleted, and the paywall now
// collects what the callback used to. So step 1 is a send instead of a round of
// calls, the paywall grew the callback's questions and the first-time details,
// and the CAM's shortlist carries three payment states instead of two.
export const paywallSpec: ExplorationSpec = {
  id: 'blurred-profile-paywall',
  sourceFile: 'front-end/designs/src/cam-admin/explore/paywall/paywall.tsx',
  title: 'Blurred-profile paywall',
  by: 'Vez',
  occasion: 'Ahead of build resourcing, w/c 18 Aug',
  stance:
    'The order of the steps is the design. Nothing here is decided — the frames exist to place the open questions on the flow.',
  readingNotes: [
    'Dom care only, per the spec. The care-home journey works differently and gets its own spec, so the only care home in here is the one a CAM makes available by hand.',
    'The round of availability calls is gone, and so has premium as a gate. Publishing the shortlist is what emails every provider on it, premium or not, so there is one CAM action rather than two: publish and send, then resend to the silence. Step 1 is a send and a wait.',
    'The paywall is one page across two frames: judging fit, then answering the questions CAM used to ask on the phone. The first-time details and the checkout sit on it too — answering and paying are one decision, so there is no separate payment screen to leave on.',
    'That page has four states, and the open one is only the first. The other three are their own section at the foot, because a full screen with its own words is the difference between a shrug and a phone call to Steph.',
  ],
  // No `thread` and no `walkthrough`: they render the collapsible at the foot,
  // which restated what the reading notes and the frames already say. The fields
  // still exist for explorations that want them.
  screen: 'desktop',
  // Full by default. These are admin pages full of table rows; at large the
  // type is too small to read the thing the frame is about.
  size: 'full',
  sections: [
    {
      id: 'F',
      // Not "the flow end to end" any more: that is the name of this page in the
      // project menu, and a section directly under it with the same words reads
      // as a mis-click rather than a jump link. This section is the strip — every
      // screen at once, which is the thing being walked through in the room.
      title: 'Every screen, in order',
      intent:
        'Where money changes hands relative to where the lead does. Today the link goes out after handover; this puts it before, and takes the callback out on the way.',
      mode: 'sequence',
      fidelity: 'sketch',
      // Every frame is flowOnly: this section is the strip, the overview that
      // gets walked through in the room. Each screen is argued in its own
      // section below, so a detail row here would say everything twice.
      frames: [
        {
          id: 'F0',
          name: 'Building the shortlist',
          flowOnly: true,
          render: () => <F0BuildShortlist />,
        },
        {
          id: 'F1',
          name: 'Sending and tracking the request',
          tap: 'Shortlist assembled',
          flowOnly: true,
          render: () => <F1SendRequest />,
        },
        {
          id: 'F1b',
          name: 'Opening a row',
          tap: 'Click a row',
          flowOnly: true,
          render: () => <F1bRowDrawer />,
        },
        {
          id: 'F2',
          name: 'The email',
          tap: 'The request lands',
          flowOnly: true,
          render: () => <F2Email />,
        },
        {
          id: 'F3',
          name: 'Blurred profile',
          tap: 'View the lead',
          flowOnly: true,
          render: () => <F3Paywall />,
        },
        {
          id: 'F4',
          name: 'Answering the request',
          tap: 'Same page, further down',
          flowOnly: true,
          render: () => <F4Answers />,
        },
        {
          id: 'F6',
          name: 'Unlocked',
          tap: 'Pay £48.00',
          flowOnly: true,
          render: () => <F6Unlocked />,
        },
        {
          id: 'F7',
          name: 'The carer is told',
          tap: 'Payment confirms',
          screen: 'phone',
          flowOnly: true,
          render: () => <F7Push />,
        },
        {
          id: 'F8',
          name: 'Shortlist, member side',
          tap: 'Open the notification',
          screen: 'phone',
          flowOnly: true,
          render: () => <F8MemberShortlist />,
        },
        {
          id: 'F9',
          name: 'Shortlist, CAM side',
          flowOnly: true,
          render: () => <F9CamShortlist />,
        },
        // The branches are aside as well as flowOnly. None of them is a step in
        // the journey, so putting them in the strip would draw an arrow that
        // isn't there. They appear only as their own sections at the foot. The
        // paywall page's other five states aren't here at all — they replace the
        // open page rather than following it, so they get their own section.
        {
          id: 'F11',
          name: 'Pending expires',
          aside: true,
          flowOnly: true,
          render: () => <F11Timeout />,
        },
        {
          id: 'F12',
          name: 'Nobody has paid',
          aside: true,
          flowOnly: true,
          render: () => <F12PublishUnpaid />,
        },
      ],
    },
    {
      id: 'F0',
      title: '0. Building the shortlist',
      bare: true,
      mode: 'probes',
      // The real screen, so not a sketch.
      fidelity: 'deep',
      frames: [
        {
          id: 'F0',
          name: 'Building the shortlist',
          story: {
            as: 'a care manager',
            iWant: 'to start a shortlist as soon as the assessment lands',
            soThat: 'the family gets real options quickly',
          },
          render: () => <F0BuildShortlist />,
        },
      ],
    },
    {
      id: 'F1',
      title: '1. Sending and tracking the request',
      bare: true,
      mode: 'probes',
      // The real screen, so not a sketch.
      fidelity: 'deep',
      frames: [
        {
          id: 'F1',
          name: 'Sending and tracking the request',
          story: {
            as: 'a care manager',
            iWant: 'to send the request once and get on with something else',
            soThat: 'I am not spending my afternoon ringing round for answers',
          },
          render: () => <F1SendRequest />,
        },
      ],
    },
    {
      id: 'F1b',
      title: '1b. Opening a row',
      bare: true,
      mode: 'probes',
      fidelity: 'deep',
      frames: [
        {
          id: 'F1b',
          name: 'Opening a row',
          story: {
            as: 'a care manager',
            iWant: 'to see what a provider told us without ringing them',
            soThat: 'the answers on the paywall replace the round of calls',
          },
          render: () => <F1bRowDrawer />,
        },
      ],
    },
    {
      id: 'F2',
      title: '2. The email',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F2',
          name: 'The email',
          story: {
            as: 'a provider',
            iWant:
              'enough in the email to know whether the lead is worth my time',
            soThat: 'I am not paying just to find out',
          },
          render: () => <F2Email />,
        },
      ],
    },
    {
      id: 'F3',
      title: '3. Blurred profile',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F3',
          name: 'Blurred profile',
          story: {
            as: 'a provider',
            iWant: 'to see the shape of the client’s needs before I pay',
            soThat: 'I can judge fit without being told who they are',
          },
          render: () => <F3Paywall />,
        },
      ],
    },
    {
      id: 'F4',
      title: '4. Answering the request',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F4',
          name: 'Answering the request',
          story: {
            as: 'a provider',
            iWant:
              'to answer against this request in a minute, not fill a form',
            soThat: 'saying yes is easier than waiting for a phone call',
          },
          render: () => <F4Answers />,
        },
      ],
    },
    {
      id: 'F6',
      title: '5. Unlocked',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F6',
          name: 'Unlocked',
          story: {
            as: 'a provider',
            iWant: 'the client’s email and their case manager',
            soThat: 'I can make contact straight away',
          },
          render: () => <F6Unlocked />,
        },
      ],
    },
    {
      id: 'F7',
      title: '6. The carer is told',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F7',
          name: 'The carer is told',
          screen: 'phone',
          copy: {
            lines: [
              'A new provider on your shortlist',
              'Riverside lodge has confirmed they can help. Have a look when you’re ready.',
            ],
          },
          story: {
            as: 'a carer',
            iWant: 'to know when a provider becomes available',
            soThat: 'I am not left checking',
          },
          render: () => <F7Push />,
        },
      ],
    },
    {
      id: 'F8',
      title: '7. Shortlist, member side',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F8',
          name: 'Shortlist, member side',
          screen: 'phone',
          story: {
            as: 'a carer',
            iWant: 'to see only the providers who can actually help',
            soThat: 'I am not comparing options that are not real',
          },
          render: () => <F8MemberShortlist />,
        },
      ],
    },
    {
      id: 'F9',
      title: '8. Shortlist, CAM side',
      bare: true,
      mode: 'probes',
      // The real screen, so not a sketch.
      fidelity: 'deep',
      frames: [
        {
          id: 'F9',
          name: 'Shortlist, CAM side',
          story: {
            as: 'a care manager',
            iWant: 'to see who has paid, who is pending, and who still owes',
            soThat: 'I know what the family can see and what I have to chase',
          },
          render: () => <F9CamShortlist />,
        },
      ],
    },
    {
      id: 'F11',
      title: 'Branch: pending expires',
      bare: true,
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'F11',
          name: 'Pending expires',
          story: {
            as: 'a carer',
            iWant: 'my shortlist published even if a provider goes quiet',
            soThat: 'waiting on them does not hold me up',
          },
          render: () => <F11Timeout />,
        },
      ],
    },
    {
      id: 'F12',
      title: 'Branch: nobody has paid',
      bare: true,
      mode: 'probes',
      // The real screen, so not a sketch.
      fidelity: 'deep',
      frames: [
        {
          id: 'F12',
          name: 'Nobody has paid',
          story: {
            as: 'a care manager',
            iWant: 'to get a good provider in front of a family today',
            soThat: 'a gap in the product does not cost them a placement',
          },
          render: () => <F12PublishUnpaid />,
        },
      ],
    },
    {
      id: 'S',
      title: 'The page in every other state',
      intent:
        'The open page is one of four. Each of the others is a full screen with its own words — never a banner bolted onto the open page, and never a generic error. The copy is a first draft; Steph owns the commercial wording.',
      mode: 'probes',
      fidelity: 'sketch',
      frames: [
        {
          id: 'S2',
          name: 'Something went wrong',
          story: {
            as: 'a provider whose payment failed',
            iWant: 'to retry without losing my place',
            soThat: 'a declined card does not cost me the lead',
          },
          render: () => <S2PaymentFailed />,
        },
      ],
    },
    // Hidden for now, commented rather than deleted.
    //
    // Section A — 'Where the shortlist lives' — the rail-vs-page comparison
    // that argues for everything the shortlist page assumes. Section B —
    // 'Opening a row' — the drawer that carries what the table drops. Both
    // will likely come back once there's a room to have the argument in.
    // A1ShortlistInRail and B2RowDrawer are untouched in Flow.tsx; uncomment
    // the block and their imports together.
    //
    // {
    //   id: 'A',
    //   title: 'Where the shortlist lives',
    //   intent:
    //     'The paywall needs somewhere to show who has paid. Today the shortlist is a narrow rail inside Care search, which is already struggling before any of that is added.',
    //   mode: 'rivals',
    //   fidelity: 'sketch',
    //   frames: [
    //     {
    //       id: 'A1',
    //       name: 'In the care search rail',
    //       inTheFrame:
    //         'The real care search, unchanged: results on the left, the shortlist in the right-hand rail with its statuses and notes.',
    //       optimises:
    //         'No new destination. You add a provider and confirm it landed without leaving the search.',
    //       costs:
    //         'At that width statuses stack instead of aligning, so they can’t be compared, and one long note pushes providers below the fold. There is no room for tier, sent, paid or on-shortlist.',
    //       chasing:
    //         'Is the rail failing at managing the list already, or only once the paywall arrives?',
    //       render: () => <A1ShortlistInRail />,
    //     },
    //     {
    //       id: 'A2',
    //       name: 'Its own page',
    //       inTheFrame:
    //         'The shortlist as a full-width page: provider, tier, availability, status, and the send action. Publish sits in the header.',
    //       optimises:
    //         'Statuses read as a column, so the state of the whole list is one glance. Room for the paywall columns without crowding anything.',
    //       costs:
    //         'A second destination, and the CAM has to leave search to manage the list.',
    //       chasing:
    //         'Is the page reached from inside the circle, or is it a Shortlists item on the nav rail? Shortlists belong to a circle, which argues for the first.',
    //       render: () => <A2ShortlistPage />,
    //     },
    //   ],
    // },
    // {
    //   id: 'B',
    //   title: 'Opening a row',
    //   intent:
    //     'A table only works if the things it drops have somewhere to go. This is where they go.',
    //   mode: 'sequence',
    //   fidelity: 'sketch',
    //   frames: [
    //     {
    //       id: 'B1',
    //       name: 'The table',
    //       flowOnly: true,
    //       inTheFrame: 'The shortlist page, as F1.',
    //       chasing: 'The row is the question, not the table.',
    //       render: () => <A2ShortlistPage />,
    //     },
    //     {
    //       id: 'B2',
    //       name: 'Row drawer',
    //       tap: 'Click a row',
    //       inTheFrame:
    //         'Provider detail, the paywall state in full (link sent, paid, on the carer’s shortlist), and the CAM’s note at full length.',
    //       chasing:
    //         'What earns a column and what belongs in the drawer. The note is the test case — it is the thing that broke the rail.',
    //       render: () => <B2RowDrawer />,
    //     },
    //   ],
    // },
  ],
};
